/**
 * ProcessingTermsDialog — the click-through processing terms (sheet 41
 * callouts 11 and 12, sheet 31 callout 13; mission 7 WP08 T044; FR-007, FR-023;
 * TX.3, T.1, A.5; contracts/ui-api.md "Spine, part 2"; spec US3 scenario 2).
 *
 * A blocking §7.13 modal: no ×, and neither Esc nor a scrim press closes it;
 * only the ghost Cancel (`onCancel`) or an acceptance (`onAccepted`) ends it,
 * and the page owns `open`. It renders `PROCESSING_TERMS.title` and
 * `PROCESSING_TERMS.text` verbatim at `body-lg` (16/24; the warranties are
 * newline-separated, so `whitespace-pre-line`). The text is the Legal
 * placeholder tagged for Q14 (C-003) and is never paraphrased here.
 *
 * "I accept" posts `PROCESSING_TERMS.version`, the version rendered, so the
 * version accepted is the one shown. While the POST is in flight the button
 * is busy and a second press sends nothing. On success the terms read is set
 * from the answer and invalidated, then `onAccepted(status)`. A refusal renders
 * through `ProblemBand` (a transport failure as the generic notice) and the
 * dialog stays open.
 *
 * A version mismatch (mission 7 review follow-up 10, UI-5; FR-022; TX.3): the
 * API refuses any version but the served one with a 400 `validation-failed` on
 * `version`. The dialog re-reads `GET /v1/processing-terms` (which also
 * refreshes the page's terms read) and posts once more, with the served
 * version, only when it is the compiled text's version: the version accepted
 * is still the one shown. A served version the compiled text is not, or a
 * second refusal, renders `reloadAndRetry` and posts nothing more (never a
 * loop). A failed re-read renders the generic notice. Radix traps focus. The page opens the dialog without a
 * `DialogTrigger`, so Radix has no trigger to return to: the element focused
 * when the dialog opened is kept and focused again on close (§9, FR-028).
 *
 * Mission 9 WP14 T090 (amendment 8; FR-015; B.1; U11, U30): while `useBillingGate` refuses (read-only,
 * pending) "I accept" is replaced by the demoted, relabeled `gatedReadOnly` button with the gate's
 * reason; the terms still read and Cancel still closes.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ApiError, type Problem } from '@/lib/api'
import { acceptProcessingTerms, type ProcessingTermsStatus } from '@/lib/api/terms'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { PROCESSING_TERMS } from '@/lib/mandatory-copy'
import { copyText } from '@/lib/provisional-copy'
import { PROBLEM } from '@/lib/problems'
import { keys, processingTermsQuery } from '@/lib/queries'

type Failure = { kind: 'problem'; problem: Problem } | { kind: 'stale' } | { kind: 'generic' }

/** The API's refusal of a version other than the served one (termsvc.MsgStaleVersion; UI-5). */
function isStaleVersion(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.problem.type === PROBLEM.VALIDATION_FAILED &&
    (err.problem.errors ?? []).some((e) => e.field === 'version')
  )
}

export interface ProcessingTermsDialogProps {
  open: boolean
  onAccepted(status: ProcessingTermsStatus): void
  onCancel(): void
}

export function ProcessingTermsDialog({ open, onAccepted, onCancel }: ProcessingTermsDialogProps) {
  const qc = useQueryClient()
  const gate = useBillingGate()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  const inFlight = useRef(false)
  const returnTo = useRef<HTMLElement | null>(null)
  // The terms paragraph is the dialog's accessible description (FR-028).
  const textId = useId()

  async function accept() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setFailure(null)
    try {
      let status: ProcessingTermsStatus
      try {
        status = await acceptProcessingTerms(PROCESSING_TERMS.version)
      } catch (err) {
        if (!isStaleVersion(err)) throw err
        // UI-5: one re-read, and at most one retry, with the served version only when it is the text shown.
        const served = await qc.fetchQuery(processingTermsQuery())
        if (served.current_version !== PROCESSING_TERMS.version) {
          setFailure({ kind: 'stale' })
          return
        }
        status = await acceptProcessingTerms(served.current_version)
      }
      qc.setQueryData(keys.processingTerms, status)
      void qc.invalidateQueries({ queryKey: keys.processingTerms })
      onAccepted(status)
    } catch (err) {
      if (isStaleVersion(err)) setFailure({ kind: 'stale' })
      else setFailure(err instanceof ApiError ? { kind: 'problem', problem: err.problem } : { kind: 'generic' })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  function cancel() {
    setFailure(null)
    onCancel()
  }

  return (
    // Any dismissal Radix reports would be a cancel, but Esc and a press outside are prevented below.
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : cancel())}>
      <DialogContent
        size="md"
        aria-describedby={textId}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={() => {
          // Runs before Radix moves focus in, so this is the opener.
          returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnTo.current?.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle>{PROCESSING_TERMS.title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <p id={textId} data-terms-text="" className="whitespace-pre-line text-[16px] leading-6 text-navy-950">
            {PROCESSING_TERMS.text}
          </p>
          {failure?.kind === 'problem' ? <ProblemBand problem={failure.problem} /> : null}
          {failure?.kind === 'stale' ? (
            <Band kind="blocked">
              <p>{copyText('reloadAndRetry')}</p>
            </Band>
          ) : null}
          {failure?.kind === 'generic' ? (
            <Band kind="blocked">
              <p>{copyText('genericError')}</p>
            </Band>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={cancel}>
            {copyText('modalCancel')}
          </Button>
          {gate.refused ? (
            // Mission 9 WP14 T090 (amendment 8; U11, U30): the gated primary, demoted and relabeled.
            <Button variant="secondary" {...gateButton(gate)}>
              {copyText('gatedReadOnly')}
            </Button>
          ) : (
            <Button variant="primary" loading={busy} onClick={() => void accept()}>
              {copyText('termsIAccept')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
